import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import {
  DescriptionOutlined,
  BrokenImageOutlined,
} from "@mui/icons-material";
import useTranslation from "../../hooks/useTranslation";
import { ChatSource } from "../models/chatBotModels";
import { dt } from "../../themes/redesignTokens";
import accountContext from "../../../account/accountContext";

// ═══════════════════════════════════════════════════════════════
//  MIDAS Design Tokens (local subset)
// ═══════════════════════════════════════════════════════════════


const midas = {
  gold: "#D4A017",
};

// ═══════════════════════════════════════════════════════════════
//  Image proxy URL builder
// ═══════════════════════════════════════════════════════════════

/**
 * Resolve a bot image_url to our proxy endpoint.
 * Bot sends either:
 *   - Relative: /api/v1/image/SBL500_manual_sk/SBL500_manual_sk_page_047.jpg
 *   - Full URL: https://machineid.chat/api/v1/image/SBL500_manual_sk/...page_047.jpg
 * Our proxy: {apiBaseUrl}/v1/bot/image/SBL500_manual_sk/SBL500_manual_sk_page_047.jpg
 *
 * URLs are normalized upstream (in mapBotSources) so they should be relative here,
 * but this function handles both forms defensively.
 */
const resolveImageProxyUrl = (imageUrl: string): string => {
  const baseUrl = (window as any).app?.config?.apiBaseUrl || "";
  // Extract the path after /api/v1/image/ — works for both relative and full URLs
  const marker = "/api/v1/image/";
  const markerIdx = imageUrl.indexOf(marker);
  const stripped = markerIdx !== -1
    ? imageUrl.slice(markerIdx + marker.length)
    : imageUrl.replace(/^\/+/, ""); // fallback: strip leading slashes
  return `${baseUrl}/v1/bot/image/${stripped}`;
};

// ═══════════════════════════════════════════════════════════════
//  ChatSourceCard
// ═══════════════════════════════════════════════════════════════

interface ChatSourceCardProps {
  source: ChatSource;
  /** Called with the blob URL when user clicks a card that has an image */
  onImageClick?: (blobUrl: string) => void;
  /** Called when image blob is loaded — parent uses this to build gallery */
  onImageLoaded?: (blobUrl: string) => void;
  /** If true, still loads image (for gallery) but renders nothing */
  hidden?: boolean;
}

const ChatSourceCard: React.FC<ChatSourceCardProps> = ({ source, onImageClick, onImageLoaded, hidden }) => {
  const { translate } = useTranslation("chatBot");
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState(false);
  // Ref tracks the current blob URL so the cleanup closure always has the latest value
  const blobUrlRef = useRef<string | null>(null);

  const hasImage = !!source.imageUrl;

  // Fetch image via authenticated request and create a blob URL for <img> src
  useEffect(() => {
    if (!source.imageUrl) return;

    let revoked = false;
    const proxyUrl = resolveImageProxyUrl(source.imageUrl);
    const token = accountContext.accessToken;

    const headers: Record<string, string> = {};
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    fetch(proxyUrl, { headers })
      .then((res) => {
        if (!res.ok) throw new Error(`Image fetch failed: ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (revoked) return;
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setThumbnailUrl(url);
        onImageLoaded?.(url);
      })
      .catch(() => {
        if (!revoked) setImageError(true);
      });

    return () => {
      revoked = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.imageUrl]);

  // Hidden cards only exist to trigger the image fetch — render nothing
  if (hidden) return null;

  const getPageLabel = (): string | null => {
    if (source.pageFrom && source.pageTo) {
      return translate("source.pages", { from: source.pageFrom, to: source.pageTo });
    }
    if (source.page) {
      return translate("source.page", { page: source.page });
    }
    return null;
  };

  const handleClick = () => {
    if (thumbnailUrl && onImageClick) {
      onImageClick(thumbnailUrl);
      return;
    }
    // Fallback: open URL in new tab (non-image sources)
    if (source.url) {
      window.open(source.url, "_blank", "noopener,noreferrer");
    }
  };

  const pageLabel = getPageLabel();
  const isClickable = !!source.url || !!thumbnailUrl;

  return (
    <Box
      onClick={handleClick}
      sx={{
        minWidth: hasImage ? "160px" : "180px",
        maxWidth: hasImage ? "180px" : "220px",
        backgroundColor: dt.cardBg,
        border: `1px solid ${dt.cardBorder}`,
        borderRadius: "8px",
        overflow: "hidden",
        cursor: isClickable ? "pointer" : "default",
        display: "flex",
        flexDirection: "column",
        flexShrink: 0,
        transition: "all 0.2s ease",
        "&:hover": isClickable
          ? {
              borderColor: midas.gold,
              boxShadow: "0 2px 8px rgba(212, 160, 23, 0.15)",
            }
          : {},
      }}
    >
      {/* Image thumbnail (if available) */}
      {hasImage && (
        <Box
          sx={{
            width: "100%",
            height: "90px",
            backgroundColor: "#F8F9FA",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderBottom: `1px solid ${dt.cardBorder}`,
            overflow: "hidden",
          }}
        >
          {thumbnailUrl && !imageError ? (
            <Box
              component="img"
              src={thumbnailUrl}
              alt={source.document}
              onError={() => setImageError(true)}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : imageError ? (
            <BrokenImageOutlined
              sx={{ fontSize: "24px", color: dt.textMuted }}
            />
          ) : (
            // Loading placeholder
            <Box
              sx={{
                width: "100%",
                height: "100%",
                background: `linear-gradient(90deg, #F0F1F3 25%, #E8E9EB 50%, #F0F1F3 75%)`,
                backgroundSize: "200% 100%",
                animation: "shimmer 1.5s infinite",
                "@keyframes shimmer": {
                  "0%": { backgroundPosition: "200% 0" },
                  "100%": { backgroundPosition: "-200% 0" },
                },
              }}
            />
          )}
        </Box>
      )}

      {/* Text content */}
      <Box
        sx={{
          padding: "8px 12px",
          display: "flex",
          alignItems: "flex-start",
          gap: "8px",
        }}
      >
        <DescriptionOutlined
          sx={{ fontSize: "16px", color: midas.gold, mt: "2px", flexShrink: 0 }}
        />
        <Box sx={{ overflow: "hidden", flex: 1 }}>
          <Box
            sx={{
              fontFamily: dt.font,
              fontSize: "12px",
              fontWeight: 600,
              color: dt.textPrimary,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {source.document}
          </Box>
          {pageLabel && (
            <Box
              sx={{
                fontFamily: dt.fontMono,
                fontSize: "11px",
                fontWeight: 500,
                color: dt.textSecondary,
                mt: "2px",
              }}
            >
              {pageLabel}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ChatSourceCard;
